import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, Star, ArrowLeft, ArrowRight } from "lucide-react";

/**
 * Testimonials — editorial pull-quote wall for the Home page.
 * Names anonymised to "Role · Property Context" until each client grants written permission
 * for full attribution. Swap real names/photos through the admin Media Kit flow when ready.
 */
const QUOTES = [
  {
    quote:
      "We audited five vendors after our previous crew missed the second-story panels for the third month running. Bespoke walked the building on Tuesday, sent a proposal on Wednesday, started service on Monday. We've never called anyone else.",
    role: "Senior Property Manager",
    context: "Class-A office portfolio · Manhattan",
    stars: 5,
    metric: "24 buildings · 3-year retainer",
  },
  {
    quote:
      "The digital service reports are what sold my board. Every visit, geotagged photos in my inbox before the crew rolls off site. Zero disputes, zero surprises. That single ritual is worth the premium.",
    role: "Head of Facilities",
    context: "Restaurant group · 12 locations",
    stars: 5,
    metric: "12 storefronts · monthly retainer",
  },
  {
    quote:
      "Our previous cleaner blew out three mortar joints on the landmark limestone. Bespoke came in with a 400 PSI soft-wash rig and it looked museum-fresh in a morning. They understood the substrate. That's what you pay for.",
    role: "Superintendent",
    context: "Historic residential · Upper East Side",
    stars: 5,
    metric: "1929-era co-op · restored 2024",
  },
  {
    quote:
      "We're a hospital campus. Weekend and holiday-only service is non-negotiable. Bespoke has never rescheduled, never overrun, never left a piece of equipment in a corridor. That reliability is why we renewed for three more years.",
    role: "Director of Operations",
    context: "Medical office park · Long Island",
    stars: 5,
    metric: "180K sq ft campus · quarterly cadence",
  },
];

export default function Testimonials() {
  const [idx, setIdx] = useState(0);
  const q = QUOTES[idx];

  return (
    <section
      className="py-32 md:py-40 relative overflow-hidden"
      style={{ background: "var(--bg-2)" }}
      data-testid="testimonials-section"
    >
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(circle at 80% 20%, rgba(198,168,124,0.06), transparent 55%)"
      }} />

      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="grid md:grid-cols-12 gap-8 mb-16"
        >
          <div className="md:col-span-2 overline pt-2">The Ledger</div>
          <div className="md:col-span-10 flex items-end justify-between flex-wrap gap-6">
            <h2 className="font-display text-5xl md:text-7xl" style={{ letterSpacing: "-0.035em", lineHeight: 0.95 }}>
              What our{" "}
              <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>clients say.</span>
            </h2>
            <div className="pill" data-testid="testimonial-counter">
              {String(idx + 1).padStart(2, "0")} / {String(QUOTES.length).padStart(2, "0")}
            </div>
          </div>
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.blockquote
            key={idx}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="glass-card p-10 md:p-16 relative"
            data-testid={`testimonial-${idx + 1}`}
          >
            <Quote size={36} strokeWidth={1.2} className="absolute top-8 md:top-10 left-8 md:left-12" style={{ color: "var(--gold)", opacity: 0.35 }} />
            <div className="flex items-center gap-1 mb-8">
              {Array.from({ length: q.stars }).map((_, i) => (
                <Star key={i} size={14} fill="var(--gold)" strokeWidth={0} style={{ color: "var(--gold)" }} />
              ))}
            </div>
            <p className="font-display leading-[1.25] max-w-4xl" style={{ fontSize: "clamp(1.6rem, 2.6vw, 2.6rem)", letterSpacing: "-0.02em", color: "var(--text)" }}>
              “{q.quote}”
            </p>
            <div className="mt-12 pt-8 flex flex-wrap items-end justify-between gap-6" style={{ borderTop: "1px solid var(--line)" }}>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.42em]" style={{ color: "var(--gold)" }}>{q.role}</div>
                <div className="mt-2 font-body" style={{ color: "var(--text-2)" }}>{q.context}</div>
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.36em]" style={{ color: "var(--text-3)" }}>{q.metric}</div>
            </div>
          </motion.blockquote>
        </AnimatePresence>

        <div className="mt-12 flex items-center justify-between flex-wrap gap-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIdx((idx - 1 + QUOTES.length) % QUOTES.length)}
              className="w-11 h-11 flex items-center justify-center"
              style={{ borderRadius: "var(--r-full)", border: "1px solid var(--line-gold)", color: "var(--gold)" }}
              data-cursor="hover" data-testid="testimonial-prev" aria-label="Previous testimonial"
            ><ArrowLeft size={15} strokeWidth={1.5} /></button>
            <button
              onClick={() => setIdx((idx + 1) % QUOTES.length)}
              className="w-11 h-11 flex items-center justify-center"
              style={{ borderRadius: "var(--r-full)", border: "1px solid var(--line-gold)", color: "var(--gold)" }}
              data-cursor="hover" data-testid="testimonial-next" aria-label="Next testimonial"
            ><ArrowRight size={15} strokeWidth={1.5} /></button>
            <div className="flex items-center gap-2 ml-3">
              {QUOTES.map((_, i) => (
                <button key={i} onClick={() => setIdx(i)} className="transition-all duration-500"
                  aria-label={`Show testimonial ${i + 1}`} data-testid={`testimonial-dot-${i}`}
                  style={{ width: i === idx ? 32 : 8, height: 3, borderRadius: 2, background: i === idx ? "var(--gold)" : "var(--line)" }}
                />
              ))}
            </div>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.36em]" style={{ color: "var(--text-3)" }}>
            Full ledger available on request · GMB reviews will feed in when profile goes live
          </div>
        </div>
      </div>
    </section>
  );
}
